import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import usePostTransaksi from '../../hooks/usePostTransaksi';
import useUpdateTransaksi from '../../hooks/useUpdateTransaksi';
import usePostKonfirmasiTransaksi from '../../hooks/usePostKonfirmasiTransaksi';
import { addNotification } from '../../Services/notifikasi';

const PAYMENT_CONFIRMATION_STORAGE_KEY = 'pending_qris_payment_confirmation';

function normalizeText(value) {
  return String(value || '')
    .replace(/\r?\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatCurrency(value) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(Number(value || 0) || 0);
}

function formatDateTime(value) {
  const parsed = value ? new Date(value) : new Date();
  if (Number.isNaN(parsed.getTime())) return '-';

  return parsed.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function KonfirmasiPembayaranUser({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentContext, setPaymentContext] = useState(() => {
    const routedState = location.state?.paymentContext;
    if (routedState) {
      sessionStorage.setItem(PAYMENT_CONFIRMATION_STORAGE_KEY, JSON.stringify(routedState));
      return routedState;
    }

    const savedState = sessionStorage.getItem(PAYMENT_CONFIRMATION_STORAGE_KEY);
    if (!savedState) return null;

    try {
      return JSON.parse(savedState);
    } catch {
      sessionStorage.removeItem(PAYMENT_CONFIRMATION_STORAGE_KEY);
      return null;
    }
  });
  const [proofFile, setProofFile] = useState(null);
  const [proofPreviewUrl, setProofPreviewUrl] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [receiptData, setReceiptData] = useState(null);

  const {
    postTransaksi,
    loading: creatingTransaction,
  } = usePostTransaksi();
  const {
    putTransaksi,
    loading: updatingTransaction,
  } = useUpdateTransaksi();
  const {
    postKonfirmasiTransaksi,
    loading: creatingConfirmation,
    error: confirmationError,
  } = usePostKonfirmasiTransaksi();

  useEffect(() => {
    return () => {
      if (proofPreviewUrl) {
        URL.revokeObjectURL(proofPreviewUrl);
      }
    };
  }, [proofPreviewUrl]);

  const createdCheckout = paymentContext?.createdCheckout || null;
  const pendingQrisCheckout = paymentContext?.pendingQrisCheckout || null;
  const selectedQrCode = paymentContext?.selectedQrCode || null;
  const donorName = normalizeText(user?.donatur?.nama || user?.nama || 'Donatur');
  const donorCode = normalizeText(
    user?.donatur?.kode_donatur ||
    paymentContext?.createdCheckout?.kode_donatur ||
    paymentContext?.pendingQrisCheckout?.kodeDonatur ||
    ''
  );

  const summary = useMemo(() => {
    const checkout = createdCheckout || {};
    return {
      total: Number(checkout?.total_donasi || 0),
      items: Array.isArray(checkout?.transactions) ? checkout.transactions : [],
      paymentMethod: checkout?.metode_pembayaran || pendingQrisCheckout?.namaMetodePembayaran || 'QRIS',
      paymentLane: checkout?.jalur_pembayaran || pendingQrisCheckout?.jalurPembayaran || 'QRIS',
      paymentType: pendingQrisCheckout?.paymentType || 'qris',
      bankAccountNumber:
        checkout?.no_rek ||
        pendingQrisCheckout?.bankAccountNumber ||
        checkout?.transactions?.[0]?.no_rek ||
        '',
    };
  }, [createdCheckout, pendingQrisCheckout]);

  const loading = creatingTransaction || updatingTransaction || creatingConfirmation;

  const handleSelectProof = (event) => {
    const file = event.target.files?.[0] || null;
    setSubmitError('');
    setSubmitMessage('');

    if (proofPreviewUrl) {
      URL.revokeObjectURL(proofPreviewUrl);
      setProofPreviewUrl('');
    }

    if (!file) {
      setProofFile(null);
      return;
    }

    setProofFile(file);
    setProofPreviewUrl(URL.createObjectURL(file));
  };

  const buildConfirmationPayloadVariants = (transactionId) => {
    const variants = [];

    const fullPayload = new FormData();
    fullPayload.append('id_transaksi', String(transactionId || ''));
    fullPayload.append('status', 'diproses');
    fullPayload.append('foto_bukti', proofFile);
    fullPayload.append('foto_konfirmasi', proofFile);
    variants.push({
      label: 'full-payload',
      payload: fullPayload,
    });

    const buktiOnlyPayload = new FormData();
    buktiOnlyPayload.append('id_transaksi', String(transactionId || ''));
    buktiOnlyPayload.append('status', 'diproses');
    buktiOnlyPayload.append('foto_bukti', proofFile);
    variants.push({
      label: 'foto-bukti-only',
      payload: buktiOnlyPayload,
    });

    const konfirmasiOnlyPayload = new FormData();
    konfirmasiOnlyPayload.append('id_transaksi', String(transactionId || ''));
    konfirmasiOnlyPayload.append('status', 'diproses');
    konfirmasiOnlyPayload.append('foto_konfirmasi', proofFile);
    variants.push({
      label: 'foto-konfirmasi-only',
      payload: konfirmasiOnlyPayload,
    });

    const withKurbanNullPayload = new FormData();
    withKurbanNullPayload.append('id_transaksi', String(transactionId || ''));
    withKurbanNullPayload.append('id_transaksi_kurban', 'null');
    withKurbanNullPayload.append('status', 'diproses');
    withKurbanNullPayload.append('foto_bukti', proofFile);
    variants.push({
      label: 'kurban-null-string',
      payload: withKurbanNullPayload,
    });

    return variants;
  };

  const persistTransactionsFromDraft = async () => {
    if (!pendingQrisCheckout?.itemsToProcess?.length) {
      return Array.isArray(createdCheckout?.transactions) ? createdCheckout.transactions : [];
    }

    const createdTransactions = [];

    for (const item of pendingQrisCheckout.itemsToProcess) {
      const isRegisteredDonatur = pendingQrisCheckout.isRegisterFlag === 1;
      const idLembagaForTransaction = isRegisteredDonatur
        ? (user?.donatur?.id_lembaga || user?.id_lembaga || pendingQrisCheckout.idLembaga || null)
        : null;

      const transaksiResponse = await postTransaksi(
        item.kode_transaksi,
        pendingQrisCheckout.kodeDonatur,
        item.kode_jenis_donasi,
        item.kode_detail_donasi,
        pendingQrisCheckout.kodeUser,
        pendingQrisCheckout.kodeHimpun,
        pendingQrisCheckout.kodeDetailTransaksi,
        item.nominal,
        'diproses',
        pendingQrisCheckout.finalCatatanDonatur,
        pendingQrisCheckout.isRegisterFlag,
        pendingQrisCheckout.namaMetodePembayaran,
        pendingQrisCheckout.jalurPembayaran,
        idLembagaForTransaction
      );

      if (!transaksiResponse) {
        throw new Error('Gagal menyimpan transaksi pembayaran.');
      }

      const transaksiData = transaksiResponse?.transaksi || transaksiResponse;
      createdTransactions.push({
        ...item,
        ...transaksiData,
        jumlah_donasi: Number(transaksiData?.jumlah_donasi || item.nominal || 0),
        status: transaksiData?.status || transaksiData?.status_transaksi || 'diproses',
        status_transaksi: transaksiData?.status_transaksi || transaksiData?.status || 'diproses',
      });
    }

    return createdTransactions;
  };

  const ensureTransactionsAreProcessing = async (transactions) => {
    const normalizedTransactions = Array.isArray(transactions) ? transactions : [];

    for (const transaksi of normalizedTransactions) {
      const transactionId = transaksi?.id || transaksi?.id_transaksi;
      if (!transactionId) {
        throw new Error('ID transaksi tidak ditemukan.');
      }

      const currentStatus = String(
        transaksi?.status_transaksi || transaksi?.status || ''
      ).toLowerCase();

      if (currentStatus === 'diproses') {
        continue;
      }

      const updateResponse = await putTransaksi(
        transactionId,
        transaksi.kode_transaksi,
        transaksi.kode_donatur,
        transaksi.kode_jenis_donasi,
        transaksi.kode_detail_donasi,
        transaksi.kode_user,
        transaksi.kode_himpun,
        transaksi.kode_detail_transaksi,
        transaksi.jumlah_donasi,
        'diproses',
        transaksi.catatan
      );

      if (!updateResponse) {
        throw new Error('Status transaksi belum berhasil diubah menjadi diproses.');
      }
    }
  };

  const submitConfirmationWithVariants = async (transactionId) => {
    const variants = buildConfirmationPayloadVariants(transactionId);
    let lastError = null;

    for (const variant of variants) {
      try {
        console.log('Mencoba payload konfirmasi transaksi:', variant.label);
        const response = await postKonfirmasiTransaksi(variant.payload);
        return response;
      } catch (error) {
        lastError = error;
        console.warn('Payload konfirmasi transaksi gagal:', variant.label, error?.message);
      }
    }

    throw lastError || new Error('Konfirmasi pembayaran belum berhasil disimpan.');
  };

  const handleSubmitConfirmation = async (event) => {
    event.preventDefault();
    setSubmitError('');
    setSubmitMessage('');

    if (!paymentContext) {
      setSubmitError('Data pembayaran tidak ditemukan. Silakan ulangi dari halaman transaksi.');
      return;
    }

    if (!proofFile) {
      setSubmitError('Silakan upload foto bukti pembayaran terlebih dahulu.');
      return;
    }

    try {
      const persistedTransactions = await persistTransactionsFromDraft();
      if (!persistedTransactions.length) {
        throw new Error('Tidak ada transaksi yang bisa dikonfirmasi.');
      }

      await ensureTransactionsAreProcessing(persistedTransactions);

      const primaryTransaction = persistedTransactions[0];
      const transactionId = primaryTransaction?.id || primaryTransaction?.id_transaksi;
      const confirmationResponse = await submitConfirmationWithVariants(transactionId);

      const confirmationData =
        confirmationResponse?.konfirmasi_transaksi ||
        confirmationResponse?.data ||
        confirmationResponse;
      const receiptNumber = `KTRX-${confirmationData?.id_konfirmasi_transaksi ||
        confirmationData?.id ||
        Date.now()
        }`;
      const receipt = {
        receiptNumber,
        confirmedAt:
          confirmationData?.created_at ||
          confirmationData?.updated_at ||
          primaryTransaction?.updated_at ||
          new Date().toISOString(),
        donorName,
        donorCode,
        transactionCode:
          primaryTransaction?.kode_transaksi ||
          selectedQrCode?.kode_transaksi ||
          '-',
        amount: summary.total || primaryTransaction?.jumlah_donasi || 0,
        paymentMethod: summary.paymentMethod,
        note:
          pendingQrisCheckout?.finalCatatanDonatur ||
          primaryTransaction?.catatan ||
          '',
        items: persistedTransactions,
        proofPreviewUrl: proofPreviewUrl || '',
      };

      setReceiptData(receipt);
      setPaymentContext((prev) => (
        prev
          ? {
            ...prev,
            createdCheckout: {
              ...(prev.createdCheckout || {}),
              transactions: persistedTransactions,
              total_donasi: summary.total || persistedTransactions.reduce(
                (total, item) => total + Number(item?.jumlah_donasi || 0),
                0
              ),
              total_item: persistedTransactions.length,
              status: 'diproses',
              kode_donatur: donorCode,
            },
          }
          : prev
      ));
      sessionStorage.removeItem(PAYMENT_CONFIRMATION_STORAGE_KEY);
      setSubmitMessage('Bukti pembayaran berhasil dikirim.');

      addNotification({
        title: 'Bukti Pembayaran Diterima',
        message: 'Pembayaran Anda sedang diverifikasi admin. Struk konfirmasi sudah tersedia.',
        userType: 'donatur',
        ...(donorCode ? { audienceKey: donorCode } : {}),
      });
    } catch (error) {
      console.error('Error submitting payment confirmation:', error);
      setSubmitError(
        error?.message ||
        'Konfirmasi pembayaran gagal diproses. Silakan coba lagi.'
      );
    }
  };

  if (!paymentContext && !receiptData) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 sm:px-6 lg:px-8 py-12">
        <div className="mx-auto max-w-2xl rounded-3xl border border-blue-100 bg-white p-8 text-center shadow-sm space-y-4">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-2.5 py-0.5 rounded border border-slate-200">
            Konfirmasi Pembayaran
          </span>
          <h1 className="text-xl font-bold text-blue-900">Data Pembayaran Tidak Ditemukan</h1>
          <p className="text-xs text-slate-600 leading-relaxed">
            Silakan ulangi proses transaksi dari halaman donasi agar sistem memiliki data checkout yang valid.
          </p>
          <button
            type="button"
            onClick={() => navigate('/transaksi')}
            className="px-5 py-2.5 rounded-xl bg-blue-900 hover:bg-blue-800 text-white text-xs font-bold shadow-sm transition inline-block"
          >
            Kembali ke Halaman Transaksi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-blue-900 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Header Hero Banner */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-950 via-blue-900 to-blue-900 rounded-3xl p-8 md:p-10 text-white shadow-md border border-blue-900 flex flex-col justify-between gap-4">
          <div className="max-w-3xl space-y-3">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-amber-400/20 text-amber-300 border border-amber-400/30 backdrop-blur-md">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
              MPZ DT • Verifikasi Pembayaran
            </span>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Konfirmasi Pembayaran</h1>
            <p className="text-xs md:text-sm text-amber-100 leading-relaxed">
              Unggah foto bukti transfer/pembayaran Anda untuk diverifikasi oleh tim administrasi MPZ DT.
            </p>
          </div>
        </div>

        {/* Tampilan Bukti Berhasil / Receipt Data Modal-Form */}
        {receiptData ? (
          <div className="bg-white rounded-3xl border border-blue-100 p-6 md:p-8 shadow-sm space-y-6 max-w-4xl mx-auto">
            {/* Header Success Badge */}
            <div className="bg-blue-50/70 border border-blue-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-blue-800 bg-blue-100 px-2.5 py-0.5 rounded border border-blue-200">
                  Status: Diproses Admin
                </span>
                <h2 className="text-lg font-bold text-blue-900">Bukti Pembayaran Berhasil Dikirim</h2>
                <p className="text-xs text-slate-600">
                  Pembayaran Anda sedang dalam tahap verifikasi admin. Berikut rincian struk konfirmasi Anda.
                </p>
              </div>
              <span className="text-xs font-bold text-blue-900 bg-white px-3 py-1.5 rounded-xl border border-blue-200 shadow-sm text-center flex-shrink-0">
                No. Ref: {receiptData.receiptNumber}
              </span>
            </div>

            {/* Rincian Transaksi */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-slate-50/60 rounded-2xl p-4 border border-slate-200/70 space-y-1">
                <span className="text-[10px] font-semibold uppercase text-slate-400">Kode Transaksi</span>
                <p className="text-xs font-bold text-blue-900">{receiptData.transactionCode}</p>
              </div>
              <div className="bg-slate-50/60 rounded-2xl p-4 border border-slate-200/70 space-y-1">
                <span className="text-[10px] font-semibold uppercase text-slate-400">Tanggal Konfirmasi</span>
                <p className="text-xs font-bold text-blue-900">{formatDateTime(receiptData.confirmedAt)}</p>
              </div>
              <div className="bg-slate-50/60 rounded-2xl p-4 border border-slate-200/70 space-y-1">
                <span className="text-[10px] font-semibold uppercase text-slate-400">Nama Donatur</span>
                <p className="text-xs font-bold text-blue-900">{receiptData.donorName}</p>
              </div>
              <div className="bg-slate-50/60 rounded-2xl p-4 border border-slate-200/70 space-y-1">
                <span className="text-[10px] font-semibold uppercase text-slate-400">Metode Pembayaran</span>
                <p className="text-xs font-bold text-blue-900">{receiptData.paymentMethod}</p>
              </div>
              <div className="bg-slate-50/60 rounded-2xl p-4 border border-slate-200/70 space-y-1 sm:col-span-2">
                <span className="text-[10px] font-semibold uppercase text-slate-400">Total Nominal Pembayaran</span>
                <p className="text-lg font-extrabold text-blue-950">{formatCurrency(receiptData.amount)}</p>
              </div>
            </div>

            {/* Itemized Program List */}
            <div className="space-y-3 pt-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900">Program Donasi Ditunaikan</h3>
              <div className="space-y-2">
                {(receiptData.items || []).map((item, index) => (
                  <div
                    key={`${item?.kode_transaksi || item?.nama_detail_donasi || 'receipt'}-${index}`}
                    className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50/50 p-3.5"
                  >
                    <div>
                      <p className="text-xs font-bold text-blue-900">
                        {index + 1}. {item?.nama_detail_donasi || item?.namaDetail || 'Donasi Program'}
                      </p>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500">
                        {item?.nama_jenis_donasi || item?.namaJenis || '-'}
                      </p>
                    </div>
                    <p className="text-xs font-bold text-blue-900">
                      {formatCurrency(item?.jumlah_donasi || item?.nominal || 0)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Uploaded Proof Preview */}
            {receiptData.proofPreviewUrl && (
              <div className="space-y-2 pt-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900">Foto Bukti Pembayaran Terunggah</h3>
                <div className="border border-slate-200 rounded-2xl overflow-hidden bg-slate-50/50 p-3 text-center">
                  <img
                    src={receiptData.proofPreviewUrl}
                    alt="Bukti pembayaran terunggah"
                    className="max-h-80 mx-auto object-contain rounded-xl border border-slate-200 bg-white"
                  />
                  <p className="text-[11px] text-slate-500 mt-2">
                    Foto bukti pembayaran berhasil diunggah dan disimpan ke server.
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-3 pt-4 border-t border-blue-50">
              <button
                type="button"
                onClick={() => navigate('/transaksi/riwayat')}
                className="w-full sm:w-auto px-5 py-3 rounded-xl bg-blue-900 hover:bg-blue-800 text-white text-xs font-bold shadow-sm transition"
              >
                Lihat Riwayat Transaksi
              </button>
              <button
                type="button"
                onClick={() => navigate('/donasi')}
                className="w-full sm:w-auto px-5 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-blue-950 text-xs font-bold shadow-sm transition"
              >
                Donasi Lagi
              </button>
            </div>
          </div>
        ) : (
          /* Form Upload Bukti Pembayaran (Normal View) */
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

            {/* Left: Summary (6 Cols) */}
            <div className="lg:col-span-6 bg-white rounded-3xl border border-blue-100 p-6 md:p-8 shadow-sm space-y-6">
              <div className="pb-3 border-b border-blue-50">
                <h2 className="text-xs font-bold uppercase tracking-wider text-blue-900">Ringkasan Tagihan Donasi</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-50/60 rounded-2xl p-3.5 border border-slate-200/70 space-y-0.5">
                  <span className="text-[10px] font-semibold uppercase text-slate-400">Kode Transaksi</span>
                  <p className="text-xs font-bold text-blue-900">
                    {selectedQrCode?.kode_transaksi || summary.items[0]?.kode_transaksi || '-'}
                  </p>
                </div>
                <div className="bg-slate-50/60 rounded-2xl p-3.5 border border-slate-200/70 space-y-0.5">
                  <span className="text-[10px] font-semibold uppercase text-slate-400">Total Nominal Donasi</span>
                  <p className="text-base font-extrabold text-blue-950">{formatCurrency(summary.total)}</p>
                </div>
                <div className="bg-slate-50/60 rounded-2xl p-3.5 border border-slate-200/70 space-y-0.5">
                  <span className="text-[10px] font-semibold uppercase text-slate-400">Nama Donatur</span>
                  <p className="text-xs font-bold text-blue-900">{donorName || '-'}</p>
                </div>
                <div className="bg-slate-50/60 rounded-2xl p-3.5 border border-slate-200/70 space-y-0.5">
                  <span className="text-[10px] font-semibold uppercase text-slate-400">Metode Pembayaran</span>
                  <p className="text-xs font-bold text-blue-900">{summary.paymentMethod || 'QRIS'}</p>
                </div>
                <div className="bg-slate-50/60 rounded-2xl p-3.5 border border-slate-200/70 space-y-0.5 sm:col-span-2">
                  <span className="text-[10px] font-semibold uppercase text-slate-400">
                    {summary.paymentType === 'bank' ? 'Nomor Rekening Tujuan' : 'Jalur Pembayaran'}
                  </span>
                  <p className="text-xs font-bold text-blue-900">
                    {summary.paymentType === 'bank'
                      ? (summary.bankAccountNumber || '-')
                      : (summary.paymentLane || '-')}
                  </p>
                </div>
              </div>

              {/* Items List */}
              <div className="space-y-3 pt-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900">Rincian Program Donasi</h3>
                <div className="space-y-2">
                  {(summary.items || []).map((item, index) => (
                    <div
                      key={`${item?.kode_transaksi || item?.nama_detail_donasi || 'item'}-${index}`}
                      className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50/50 p-3.5 text-xs"
                    >
                      <div>
                        <p className="font-bold text-blue-900">
                          {index + 1}. {item?.nama_detail_donasi || item?.namaDetail || 'Donasi Program'}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">
                          {item?.nama_jenis_donasi || item?.namaJenis || '-'}
                        </p>
                      </div>
                      <p className="font-bold text-blue-900">
                        {formatCurrency(item?.jumlah_donasi || item?.nominal || 0)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Upload Form (6 Cols) */}
            <form onSubmit={handleSubmitConfirmation} className="lg:col-span-6 bg-white rounded-3xl border border-blue-100 p-6 md:p-8 shadow-sm space-y-6">
              <div className="pb-3 border-b border-blue-50">
                <h2 className="text-xs font-bold uppercase tracking-wider text-blue-900">Upload Bukti Pembayaran</h2>
              </div>

              <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-blue-200 bg-slate-50/60 p-6 text-center hover:border-blue-400 hover:bg-white transition-all">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleSelectProof}
                  className="hidden"
                />
                {proofPreviewUrl ? (
                  <div className="w-full space-y-3">
                    <img
                      src={proofPreviewUrl}
                      alt="Pratinjau bukti pembayaran"
                      className="max-h-80 w-full object-contain bg-white rounded-xl border border-slate-200"
                    />
                    <p className="text-xs font-bold text-blue-900">{proofFile?.name || 'File Bukti Pembayaran'}</p>
                    <p className="text-[11px] text-slate-500">Klik area di atas jika ingin mengganti foto bukti.</p>
                  </div>
                ) : (
                  <div className="space-y-2 py-6">
                    <p className="text-xs font-bold text-blue-900">Klik untuk memilih file bukti transfer / bayar</p>
                    <p className="text-[11px] text-slate-500">Format gambar yang didukung: JPG, JPEG, PNG (Maks. 5MB)</p>
                  </div>
                )}
              </label>

              {(submitError || confirmationError) && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3.5 text-xs text-red-700 font-semibold">
                  {submitError || confirmationError}
                </div>
              )}

              {submitMessage && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-3.5 text-xs text-blue-800 font-semibold">
                  {submitMessage}
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:flex-1 py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-blue-950 text-xs font-bold shadow-sm transition disabled:opacity-50"
                >
                  {loading ? 'Menyimpan konfirmasi...' : 'Konfirmasi dan Simpan Bukti'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/transaksi')}
                  className="w-full sm:w-auto px-4 py-3 rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 transition"
                >
                  Batal
                </button>
              </div>
            </form>

          </div>
        )}

      </div>
    </div>
  );
}

export default KonfirmasiPembayaranUser;
